import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY_ITEMS } from "@/data/siteData";
import SEO from "@/components/SEO";

const TABS = ["All", "Factory", "Products"];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] } }),
};

export default function GalleryPage() {
  const [tab, setTab] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const items = useMemo(() => (
    tab === "All" ? GALLERY_ITEMS : GALLERY_ITEMS.filter((item) => item.category === tab)
  ), [tab]);

  const openLightbox = (index) => setLightbox(index);
  const closeLightbox = () => setLightbox(null);
  const prev = () => setLightbox((p) => (p > 0 ? p - 1 : items.length - 1));
  const next = () => setLightbox((p) => (p < items.length - 1 ? p + 1 : 0));

  return (
    <div data-testid="gallery-page" className="pt-16">
      <SEO
        title="Gallery - Factory & Product Showcase"
        description="Browse JSY's gallery featuring factory operations, machinery, and finished packaging products for textile brands."
        image="/images/factory-floor-interior.jpeg"
      />
      {/* Hero */}
  <section className="py-12 px-6 bg-[#F4F5F7]">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="overline">Gallery</span>
            <h1 className="mt-3 text-5xl tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}>
              See Our <span className="text-[#1D4ED8]">Work</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Filter + Grid */}
  <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div data-testid="gallery-tabs" className="flex gap-2 mb-10">
            {TABS.map((t) => (
              <button
                key={t}
                data-testid={`gallery-tab-${t.toLowerCase()}`}
                onClick={() => setTab(t)}
                className={`px-5 py-2 text-sm font-medium rounded-sm border transition-all duration-200 ${
                  tab === t
                    ? "bg-[#1D4ED8] text-white border-[#1D4ED8]"
                    : "bg-white text-[#71717A] border-[#E4E4E7] hover:border-[#1D4ED8] hover:text-[#1D4ED8]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((item, i) => (
              <motion.div
                key={item.id}
                data-testid={`gallery-item-${i}`}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
                className="border border-[#E4E4E7] rounded overflow-hidden cursor-pointer group"
                onClick={() => openLightbox(i)}
              >
                <div className="h-64 overflow-hidden relative">
                  <img src={item.image_url} alt={item.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-end">
                    <div className="p-4 translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                      <p className="text-white text-sm font-medium">{item.title}</p>
                      <p className="text-white/70 text-xs">{item.category}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {items.length === 0 && (
            <p className="text-center text-[#71717A] py-12">No gallery items found.</p>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && items[lightbox] && (
          <motion.div
            data-testid="lightbox-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lightbox-overlay"
            onClick={closeLightbox}
          >
            <button
              data-testid="lightbox-close"
              onClick={closeLightbox}
              className="absolute top-6 right-6 text-white/70 hover:text-white z-10"
            >
              <X size={28} />
            </button>
            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-6 text-white/70 hover:text-white z-10"
            >
              <ChevronLeft size={32} />
            </button>
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-6 text-white/70 hover:text-white z-10"
            >
              <ChevronRight size={32} />
            </button>
            <div onClick={(e) => e.stopPropagation()} className="text-center">
              <img src={items[lightbox].image_url} alt={items[lightbox].title} className="lightbox-img" />
              <p className="text-white mt-4 text-sm">{items[lightbox].title}</p>
              <p className="text-white/50 text-xs mt-1">{items[lightbox].description}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
